import { computed, ref, useLocalStorageVueuse, watch } from '@/composition/vue/compositionApi'

import { getRouterService, getTranslateService } from '@/core/container/ioc'

import type {
  MainSidebarMenuItem,
  MainSidebarMenuLinkItem,
  MainSidebarMenuSection,
} from '@/components/layout/pageContent/sidebar/types'
import { ScheduledType } from '@/components/views/scheduled/types'

import { ScheduledRoutes } from '@/routes/user/scheduled/types'

import { ScheduledEmailStatus } from '@/data/repositories/domain/scheduledEmail/types'

import type { ValueOf } from '@/types'

type ScheduledRoutesInfoType = 'sms' | 'email' | 'common'

export const activeMap = {
  upcoming: ref(false),
  paused: ref(false),
  notSent: ref(false),
  completed: ref(false),
}

export const useMainSidebarScheduledSMSSections = () => {
  const onToggle = (scheduledType: ScheduledType, value: boolean) => {
    activeMap.upcoming.value = false
    activeMap.paused.value = false
    activeMap.notSent.value = false
    activeMap.completed.value = false

    switch (scheduledType) {
      case ScheduledType.UPCOMING:
        activeMap.upcoming.value = value
        break
      case ScheduledType.PAUSED:
        activeMap.paused.value = value
        break
      case ScheduledType.NOT_SENT:
        activeMap.notSent.value = value
        break
      case ScheduledType.COMPLETED:
        activeMap.completed.value = value
        break
      default:
        break
    }
  }

  return {
    onToggle,
  }
}

export const useMainSidebadScheduledEmailSections = () => {
  const onToggle = (scheduledType: ScheduledEmailStatus, value: boolean) => {
    activeMap.upcoming.value = false
    activeMap.paused.value = false
    activeMap.notSent.value = false
    activeMap.completed.value = false

    switch (scheduledType) {
      case ScheduledEmailStatus.New:
        activeMap.upcoming.value = value
        break
      case ScheduledEmailStatus.Paused:
        activeMap.paused.value = value
        break
      case ScheduledEmailStatus.Completed:
        activeMap.completed.value = value
        break
      default:
        break
    }
  }

  return {
    onToggle,
  }
}

export const useMainSidebarScheduledMenu = () => {
  const routerService = getRouterService()
  const translateService = getTranslateService()

  const scheduledRoutesInfo: Record<ScheduledRoutes, ScheduledRoutesInfoType> = {
    [ScheduledRoutes.scheduled]: 'sms',
    [ScheduledRoutes.scheduledIndex]: 'common',
    [ScheduledRoutes.details]: 'sms',
    [ScheduledRoutes.detailsEmail]: 'email',
    [ScheduledRoutes.upcoming]: 'common',
    [ScheduledRoutes.upcomingOverview]: 'sms',
    [ScheduledRoutes.upcomingOverviewEmail]: 'email',
    [ScheduledRoutes.paused]: 'common',
    [ScheduledRoutes.pausedOverview]: 'sms',
    [ScheduledRoutes.pausedOverviewEmail]: 'email',
    [ScheduledRoutes.notSent]: 'common',
    [ScheduledRoutes.notSentOverview]: 'sms',
    [ScheduledRoutes.notSentOverviewEmail]: 'email',
    [ScheduledRoutes.completed]: 'common',
    [ScheduledRoutes.completedOverview]: 'sms',
    [ScheduledRoutes.completedOverviewEmail]: 'email',
    [ScheduledRoutes.calendar]: 'common',
    [ScheduledRoutes.edit]: 'sms',
    [ScheduledRoutes.editEmail]: 'email',
  }

  const smsItems = computed<MainSidebarMenuLinkItem[]>(() => [
    {
      itemType: 'link',
      title: translateService.t('scheduledSideBar.upcoming'),
      active: activeMap.upcoming,
      route: { name: ScheduledRoutes.upcoming },
    },
    {
      itemType: 'link',
      title: translateService.t('scheduledSideBar.paused'),
      active: activeMap.paused,
      route: { name: ScheduledRoutes.paused },
    },
    {
      itemType: 'link',
      title: translateService.t('scheduledSideBar.notSent'),
      active: activeMap.notSent,
      route: { name: ScheduledRoutes.notSent },
    },
    {
      itemType: 'link',
      title: translateService.t('scheduledSideBar.completed'),
      active: activeMap.completed,
      route: { name: ScheduledRoutes.completed },
    },
    {
      itemType: 'link',
      title: translateService.t('scheduledSideBar.calendar'),
      route: { name: ScheduledRoutes.calendar },
    },
  ])

  const emailItems = computed<MainSidebarMenuLinkItem[]>(() => [
    {
      itemType: 'link',
      title: translateService.t('scheduledSideBar.upcoming'),
      active: activeMap.upcoming,
      route: { name: ScheduledRoutes.upcomingOverviewEmail },
    },
    {
      itemType: 'link',
      title: translateService.t('scheduledSideBar.paused'),
      active: activeMap.paused,
      route: { name: ScheduledRoutes.pausedOverviewEmail },
    },
    {
      itemType: 'link',
      title: translateService.t('scheduledSideBar.notSent'),
      active: activeMap.notSent,
      route: { name: ScheduledRoutes.notSentOverviewEmail },
    },
    {
      itemType: 'link',
      title: translateService.t('scheduledSideBar.completed'),
      active: activeMap.completed,
      route: { name: ScheduledRoutes.completedOverviewEmail },
    },
    {
      itemType: 'link',
      title: translateService.t('scheduledSideBar.calendar'),
      route: { name: ScheduledRoutes.calendar },
    },
  ])

  const isScheduledEmailPage = useLocalStorageVueuse('tm::scheduledMenu::isEmailPage', false)

  const items = computed<MainSidebarMenuLinkItem[]>(() => {
    return isScheduledEmailPage.value ? emailItems.value : smsItems.value
  })

  const menu = computed<MainSidebarMenuItem>(() => {
    const title = translateService.t('pageTitle.user.scheduled.scheduled')

    const icon = 'event'

    const section: MainSidebarMenuSection = {
      items: items.value,
    }

    return {
      key: 'scheduled',
      itemType: 'collapse',
      title,
      icon,
      sections: [section],
      dependencies: [
        { name: ScheduledRoutes.details },
        { name: ScheduledRoutes.detailsEmail },
        { name: ScheduledRoutes.edit },
        { name: ScheduledRoutes.editEmail },
      ],
    }
  })

  const currentRouteName = computed(() => routerService.getCurrentRoute().name)

  watch(
    currentRouteName,
    (routeName) => {
      if (!routeName) {
        return
      }
      const routeInfo: ValueOf<typeof scheduledRoutesInfo> = scheduledRoutesInfo[routeName]
      if (routeInfo === 'common' || routeInfo === undefined) {
        return
      }

      isScheduledEmailPage.value = routeInfo === 'email'
    },
    {
      immediate: true,
    },
  )

  return {
    menu,
  }
}
